import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../axiosInstance';
import axios from 'axios';
import { UserContext } from '../../../UserContext'; 
import './Upload.css';
import { Helmet } from 'react-helmet';
import MountCustom from '../MountCustom';
import { PriceContext } from '../Context/PriceContext';
import Validation from '../LoginSignup/LoginSignupValidation';
import { AtomSpinner} from 'react-epic-spinners';
import AlertBanner from '../Banner/AlertBanner';



const API_URL = process.env.REACT_APP_API_URL;

const Upload = () => {
  const { email, setEmail} = useContext(UserContext); 
  const [files, setFiles] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const [instructions, setInstructions] = useState("");
  const navigate = useNavigate();
  const { paymentData, setPaymentData } = useContext(PriceContext);
  const [showAlert, setShowAlert] = useState(false);
  // Add to your useState declarations at the top:
  const [isUploading, setIsUploading] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");

  const handleFileChange = (event) => {
    const uploadedFiles = Array.from(event.target.files); // Convert to array
    let validFiles = [];
    let total = 0;

    // Validate each file thoroughly
    uploadedFiles.forEach(file => {
      // 1. Validate basic file structure
      if (!file || !file.name || !file.size || !file.type) {
        console.error('Invalid file object:', file);
        return;
      }

      // 2. Check file type
      if (!isValidFileType(file.name)) {
        alert(`Unsupported file type: ${file.name}`);
        return;
      }

      // 3. Check size limits
      const fileSizeMB = file.size / (1024 * 1024);
      if (total + fileSizeMB > 10) {
        const fileSizeKB = file.size / 1024;
        alert(`Cannot add ${file.name}. \n${fileSizeKB.toFixed(0)}KB (${fileSizeMB.toFixed(2)}MB) would exceed the 10MB limit.`);
        return;
      }

      // 4. Create clean file object with required properties
      const validFile = new File([file], file.name, {
        type: file.type,
        lastModified: file.lastModified
      });

      validFiles.push(validFile);
      total += fileSizeMB;
    });

    // Update state with only validated files
    setFiles(prevFiles => [...prevFiles, ...validFiles]);
    setTotalSize(prevTotal => prevTotal + total);
  };

  const isValidFileType = (fileName) => {
    if (!fileName || typeof fileName !== 'string') return false;
    
    const allowedExtensions = ['.jpeg', '.jpg', '.png', '.docx', '.doc', '.xlsx', '.xls', '.pdf', '.mp3', '.mp4'];
    const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    return allowedExtensions.includes(extension);
  };
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (files.length === 0) {
      alert('Please select at least one file to upload.');
      return;
    }

    setShowOverlay(true);
  };

  const handleOverlaySubmit = async () => {
    if (!email) {
        alert('Please enter your primary email.');
        return;
    }
    
    const validationErrors = Validation({ email, instructions });
    if (validationErrors.email || !instructions) {
      alert('Please provide complete and accurate information.');
      return;
    }
    setIsUploading(true);


    try {
        const statusResponse = await axiosInstance.get(`${API_URL}/users/status`, {
            params: { email },
            headers: {
                'X-Client-Version': process.env.REACT_APP_VERSION
            },
            withCredentials: true 
        });
        const statusData = statusResponse.data;

        // New version check
        if (statusResponse.status === 426 || statusData.error === "Client outdated") {
            alert(`🔄 Update required! Please refresh to use the latest version.`);
            window.location.reload(true);
            return;
        }
        // Custom 222 - User Not Found 
        if (statusResponse.status === 222) {
            alert(statusData.message || "Account not found. Please register first.");
            navigate('/login-signup');
            return;
        }

        // Existing blocked user check
        if (statusData.status === 'blocked') {
            setAlertMessage(`Account suspended (${statusData.reason}). Transactions linked to ${email} are non-refundable, not subject to dispute, and will result in bans on associated accounts.`);
            setShowAlert(true);
            setIsUploading(false);

            setTimeout(() => {
                navigate('/login-signup');
            }, 5000);

            return;
        }

        setShowOverlay(false);

        const formData = new FormData();
        files.forEach(file => {
            if (!file.name || !file.type) {
              throw new Error(`Invalid file detected: ${JSON.stringify(file)}`);
            }
            formData.append('files', file, file.name); // Explicit filename
            formData.append('instructions', instructions);
            formData.append('email', email);
          });
          for (let [key, val] of formData.entries()) {
            console.log(key, val instanceof File ? 
              `${val.name} (${val.type}, ${val.size} bytes)` : 
              val
            );
          }
        

        setPaymentData(prevData => ({
            ...prevData,
            email,
            instructions,
            uploadedFiles: files
        }));

      const response = await axios.post(`${API_URL}/upload`, formData, {
        withCredentials: true,
        headers: {
          'X-Client-Version': process.env.REACT_APP_VERSION,
          
          'Content-Type': 'multipart/form-data' // Explicitly set
        },
        transformRequest: (data) => data // Prevent any data transformation
      });

      if (response.status === 200) {
          const responseData = response.data;

          if (responseData.sessionId) {
              sessionStorage.setItem('sessionId', responseData.sessionId);
          }

          setPaymentData(prevData => ({
              ...prevData,
              email,
              instructions,
              uploadedFiles: response.data.files.map(f => f.url),
              fileNames: response.data.files.map(f => f.filename)
          }));

          alert('Files uploaded successfully!');
          setFiles([]);
          setTotalSize(0);
          setInstructions("");
          setEmail("");
          navigate('/confirmation-payment');
      } else {
          alert('Failed to upload files');
      }
    } catch (error) {
        if (error.response?.status === 426) {
            alert("Update required! Clear your browser history and reload the page.");
            window.location.reload(true);
        } else {
            console.error('Error checking user status or uploading files:', error);
            alert('Oops! Your device appears to have a connectivity issue. Please check your firewall settings, clear your cookie cache, or verify your internet connection.');
        }
      } finally {
          setIsUploading(false);
      }
  };

  useEffect(() => {
    const missingInfo = !paymentData.deliveryTurnaround || !paymentData.wordcount || !paymentData.documentType;
    if (missingInfo) {
      navigate('/');
    }
  }, [paymentData, navigate]);

  return (
    <div className="setter">
      <Helmet>
        <title>Supporting Documentation - Contranorm Media & Production</title>
      </Helmet>
      <div className="background-avatar">
       
      </div>
      <MountCustom>
        <div className="page">
  
          {showOverlay ? (
            // Overlay View
            <>
              <h1>
                <div className="redRock">Supporting Documents</div>
              </h1>
              <div className="underline"></div>
              <div className="upload_container">
  
              <textarea
                  className="clientInstructions"
                  value={instructions}
                  onChange={(e) => {
                    const text = e.target.value;
                    setInstructions(text.slice(0, 520)); // limit to 500 characters
                  }}
                  placeholder="Tell us what you had in mind."
                  required
                />

                {/* Conditionally render character count if instructions are not empty */}
                {instructions.length > 0 && (
                  <span 
                    style={{ 
                      marginTop: '-15px', 
                      marginBottom:'15px',
                      fontSize: '12px', 
                      color: instructions.length >= 500 ? '#dc899f' : '#5d1073' 
                    }}
                  >
                    {instructions.length}/500 
                  </span>
                )}

                <input
                  className="email"
                  type="email"
                  placeholder="Login Credentials (Email):"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  required
                />
              </div>
              <div className="submit-container">
                {isUploading ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', top: '-200px' }}>
                  <AtomSpinner color="#5d1073" size={100} />
                  <p style={{ fontSize: '11px' }}></p>
                </div>
                
                ) : (
                  <button className="submit" onClick={handleOverlaySubmit}>Confirm</button>
                )}
              </div>
            </>
          ) : (
            // Upload Form View
            <>
              <h1>
                <div className="redRock">Supporting Documentation</div>
              </h1>
             <br />
              <h3>Upload all files required to process your request.</h3>
              <div className="underline"></div>
              <div className="upload_container">
                <form onSubmit={handleSubmit} encType="multipart/form-data">
                  <input
                    type="file"
                    id="file-input"
                    accept=".jpeg, .jpg, .png, .docx, .doc, .xlsx, .xls, .pdf, .mp3, .mp4"
                    multiple
                    onChange={handleFileChange}
                  />
                  <label htmlFor="file-input">Select</label>
                  <div id="num-of-files">
                    Total File Size({totalSize.toFixed(2)} MB)
                  </div>
                  <ul id="files-list">
                    {files.map((file, index) => (
                      <li key={index}>
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
                      </li>
                    ))}
                  </ul>
                  <div className="submit-container">
                    <button className="submit" type="submit">Submit</button>
                  </div>
                </form>
              </div>
            </>
          )}
  
          <AlertBanner 
          showBanner={showAlert} 
          handleCancel={() => setShowAlert(false)}
          alertMessage={alertMessage}
          />
            
              
              
        
        </div>
      </MountCustom>
    </div>
  );
  
};

export default Upload;
